import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Check,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  GraduationCap,
  Layers,
  LifeBuoy,
  type LucideIcon,
  ShoppingBag,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../../hooks';
import { uiReducer } from '../../../store/reducers/ui.reducer';
import { userReducer } from '../../../store/reducers/user.reducer';
import { getShowPaywall, getShowTutorials } from '../../../store/selectors/ui.selector';
import { getUser } from '../../../store/selectors/user.selector';
import { TIPS_POPUP_FLAG } from '../../../utils/featureFlagKeys';
import { TipVisual } from './TipVisual';
import { preloadTipVisuals } from './tipImages';
import { TIP_IDS, type TipId, localDayKey, shouldAutoOpenTips, startTipIndex, tipsSeenAfter } from './tipsProgress';

const { setShowTutorials } = uiReducer.actions;
const { updateUser } = userReducer.actions;

const TIP_ICONS: Record<TipId, LucideIcon> = {
  welcome: Sparkles,
  examples: FolderOpen,
  layers: Layers,
  optimize: Wand2,
  market: ShoppingBag,
  help: LifeBuoy,
};

/** Day the popup last opened by itself, kept per browser so a reload doesn't show it twice. */
const LAST_SHOWN_KEY = 'taman.tipsShownOn';

const readLastShown = (): string | null => {
  try {
    return localStorage.getItem(LAST_SHOWN_KEY);
  } catch {
    return null;
  }
};

const writeLastShown = (day: string) => {
  try {
    localStorage.setItem(LAST_SHOWN_KEY, day);
  } catch {
    // Storage blocked (private mode): the popup may show again on the next load, which is harmless.
  }
};

/**
 * Tips & tricks shown after login: new users go through the basics from the welcome tip,
 * returning users get the next tip they haven't seen, at most once a day, until they turn it off.
 * Progress (`tipsSeen`) and the opt-out (`hideTipsPopup`) live in the user settings.
 */
export const TipsPopup: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isEnabled = useAppSelector((s) => s.featureFlags.userFeatureFlags.includes(TIPS_POPUP_FLAG));
  const isLoadingFlags = useAppSelector((s) => s.featureFlags.isLoadingUserFeatureFlags);
  const isOtherModalOpen = useAppSelector((s) => getShowPaywall(s) || getShowTutorials(s));

  const [isAutoOpenPending, setIsAutoOpenPending] = useState(false);
  const [isAutoOpen, setIsAutoOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const viewed = useRef(new Set<number>());
  const autoOpenHandled = useRef(false);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  // A pending auto-open waits behind the paywall or tutorials instead of stacking on top of them.
  const isOpen = isEnabled && !isOtherModalOpen && isAutoOpen;
  const tipsSeen = user?.settings?.tipsSeen;
  const hidden = user?.settings?.hideTipsPopup;

  // Decide once per page load, after both the user and their flags are known.
  useEffect(() => {
    if (autoOpenHandled.current || !user || isLoadingFlags) return;
    autoOpenHandled.current = true;
    const today = localDayKey();
    if (!shouldAutoOpenTips({ enabled: isEnabled, hidden, lastShownOn: readLastShown(), today })) return;
    writeLastShown(today);
    setIsAutoOpenPending(true);
  }, [user, isLoadingFlags, isEnabled, hidden]);

  // Small delay so the page underneath renders first.
  useEffect(() => {
    if (!isAutoOpenPending) return;
    const timer = setTimeout(() => setIsAutoOpen(true), 800);
    return () => clearTimeout(timer);
  }, [isAutoOpenPending]);

  // Every opening starts where the user left off.
  useEffect(() => {
    if (!isOpen) return;
    const start = startTipIndex(tipsSeen);
    viewed.current = new Set([start]);
    setIndex(start);
    setDontShowAgain(Boolean(hidden));
    preloadTipVisuals();
    nextButtonRef.current?.focus();
    // Only on open: later settings updates (our own save) must not move the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const goTo = useCallback((next: number) => {
    const wrapped = (next + TIP_IDS.length) % TIP_IDS.length;
    viewed.current.add(wrapped);
    setIndex(wrapped);
  }, []);

  const close = useCallback(
    (then?: () => void) => {
      setIsAutoOpen(false);
      dispatch(
        updateUser({
          tipsSeen: tipsSeenAfter(tipsSeen, viewed.current.size),
          hideTipsPopup: dontShowAgain,
        }),
      );
      then?.();
    },
    [dispatch, tipsSeen, dontShowAgain],
  );

  // Capture phase, so workspace shortcuts (arrows move the lamp, Delete removes it, ⌘Z…) don't fire under the popup.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') goTo(index + 1);
      else if (e.key === 'ArrowLeft') goTo(index - 1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, close, goTo, index]);

  if (!isOpen) return null;

  const tipId = TIP_IDS[index];
  const Icon = TIP_ICONS[tipId];
  const isLast = index === TIP_IDS.length - 1;

  return (
    // Above the navbar (MUI AppBar 1100) so the dialog is truly modal; toasts and tooltips stay on top.
    <div
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 backdrop-blur-[2px] px-4"
      onClick={() => close()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tips-popup-title"
        className="relative w-full max-w-[620px] rounded-xl border border-white/10 bg-[#1c1c1c] text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => close()}
          aria-label={t('tips.close')}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md bg-black/45 text-white/80 backdrop-blur-sm hover:bg-black/70 hover:text-white transition-colors cursor-pointer"
        >
          <X size={18} />
        </button>

        <TipVisual tipId={tipId} />

        <div className="px-8 pt-5 pb-6">
          <div className="flex items-center gap-2 text-[#B3EC49]">
            <Icon size={16} />
            <span className="text-xs font-medium uppercase tracking-wider text-white/55">
              {t('tips.counter', { current: index + 1, total: TIP_IDS.length })}
            </span>
          </div>

          <h2 id="tips-popup-title" className="mt-2 text-[22px] font-semibold leading-snug">
            {t(`tips.items.${tipId}.title`)}
          </h2>
          <p className="mt-2.5 min-h-[78px] text-[15px] leading-relaxed text-gray-200">
            {t(`tips.items.${tipId}.body`)}
          </p>

          <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
            {TIP_IDS.map((id, i) => (
              <button
                key={id}
                tabIndex={-1}
                onClick={() => goTo(i)}
                className={`h-[5px] rounded-full transition-all duration-300 cursor-pointer ${
                  i === index ? 'w-3 bg-[#B3EC49]' : 'w-[5px] bg-[#D9D9D9] opacity-40 hover:opacity-80'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-8 py-4">
          <label className="group flex items-center gap-2.5 text-sm text-white/65 cursor-pointer select-none transition-colors hover:text-white/90">
            {/* Real checkbox for keyboard and screen readers; the box next to it is the visible one. */}
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border border-white/30 bg-black/40 transition-colors group-hover:border-white/60 peer-checked:border-[#B3EC49] peer-checked:bg-[#B3EC49] peer-focus-visible:ring-2 peer-focus-visible:ring-[#B3EC49]/60 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#1c1c1c]"
            >
              {dontShowAgain && <Check size={13} strokeWidth={3} className="text-black" />}
            </span>
            {t('tips.dontShowAgain')}
          </label>

          <div className="flex items-center gap-2">
            <button
              onClick={() => close(() => dispatch(setShowTutorials(true)))}
              className="flex items-center gap-1.5 rounded px-3 py-2 text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
            >
              <GraduationCap size={16} />
              {t('tips.watchTutorials')}
            </button>
            <button
              onClick={() => goTo(index - 1)}
              aria-label={t('tips.previous')}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-white/20 text-white/80 hover:bg-white/10 transition-colors cursor-pointer"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              ref={nextButtonRef}
              onClick={() => (isLast ? close() : goTo(index + 1))}
              className="flex h-10 items-center gap-1 rounded-md bg-[#B3EC49] px-4 text-sm font-semibold text-black hover:bg-[#c4f26a] transition-colors cursor-pointer"
            >
              {isLast ? t('tips.done') : t('tips.next')}
              {!isLast && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
